import React, { useState } from 'react';

import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import DialogPaper from '../DialogPaper';

import SelectOtherNumberDialog from './SelectOtherNumberDialog';
import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';

const useStampStyles = makeStyles((theme) => ({
  stampContainer: ({ highlight }: { highlight: boolean }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: theme.palette.grey[400],
    backgroundColor: highlight
      ? theme.palette.primary.main
      : theme.palette.common.white,
    // Fill the grid cell (square). Capped by both a px max (large tablets) and
    // viewport height, so on short landscape screens the two rows still fit.
    width: '100%',
    maxWidth: 'min(140px, 20vh)',
    aspectRatio: '1 / 1',
    borderRadius: 5,
    cursor: 'pointer',
    '&:active': {
      backgroundColor: theme.palette.primary.main,
    },
  }),
  stampNumber: ({ highlight }: { highlight: boolean }) => ({
    color: highlight ? theme.palette.common.white : theme.palette.grey[400],
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: 'clamp(22px, 4.8vh, 42px)',
  }),
}));

const useStyles = makeStyles((theme) => ({
  stampContainer: {
    // 4-column grid so the number pad is always evenly spaced; the container
    // width (and therefore each cell) scales with the viewport.
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 'clamp(8px, 2.5vmin, 28px)',
    justifyItems: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: 'min(88vw, 640px)',
    marginTop: theme.spacing(),
    marginBottom: 'clamp(8px, 4vh, 64px)',
  },
  tile: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
    width: '100%',
    minHeight: '60vh',
  },
  title: {
    marginTop: 'clamp(4px, 1vh, 12px)',
    fontWeight: theme.typography.fontWeightBold,
    // Scales with the dialog height so the number grid below stays on-screen in
    // short (landscape) dialogs, and is additionally capped by viewport width so
    // the heading doesn't overwhelm narrow phone screens.
    fontSize: 'clamp(27px, min(6vh, 8vw), 52px)',
    textAlign: 'center',
    marginBottom: 'clamp(16px, 4vh, 48px)',
  },
  box: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  close: {
    marginLeft: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
}));
//------------------------------------------------------------------------------
// Validation
//------------------------------------------------------------------------------

type Props = Pick<FullScreenDialogProps, 'open'> & {
  onClose: () => void;
  confirmSelectedNumber: (selectedNum: string) => void;
  maxSelectedNumber?: number;
  maxStampIssued: number;
  onlyScanOnce: boolean;
  stampNum: number;
};

export default function SelectNumberStampDialog({
  onClose,
  open,
  confirmSelectedNumber,
  maxSelectedNumber,
  maxStampIssued,
  onlyScanOnce,
  stampNum,
}: Props) {
  const initStampArray: string[] = ['1', '2', '3', '4', '5', '6', '7', '+'];
  const [selectOtherNumberOpenDialog, setSelectOtherNumberOpenDialog] =
    useState(false);
  const [selectedStamp, setSelectedStamp] = useState(0);
  const classes = useStyles();
  const [stampArray, setStampArray] = useState(initStampArray);
  const restrictedStringNumbers: string[] = Array.from(
    { length: stampNum },
    (_, index) => (index + 1).toString(),
  );
  const maxStampStringNumbers: string[] = Array.from(
    { length: maxStampIssued },
    (_, index) => (index + 1).toString(),
  );

  function Stamp({
    value,
    highlight,
    handleClick,
  }: {
    value: string;
    highlight: boolean;
    handleClick: () => void;
  }) {
    const classes = useStampStyles({ highlight });

    return (
      <div className={classes.stampContainer} onClick={handleClick}>
        <Typography variant="h4" className={classes.stampNumber}>
          {value}
        </Typography>
      </div>
    );
  }

  const renderStamps = () => {
    let arrayStamp: string[] = [];
    if (onlyScanOnce) {
      arrayStamp = restrictedStringNumbers;
    } else {
      if (maxStampIssued > 8) {
        arrayStamp = stampArray;
      } else {
        arrayStamp = maxStampStringNumbers;
      }
    }

    return arrayStamp.map((stmp, idx) => (
      <div key={idx} className={classes.tile}>
        <Stamp
          value={stmp}
          highlight={stmp === selectedStamp.toString(10)}
          handleClick={() => {
            if (stmp === '+') {
              setSelectOtherNumberOpenDialog(true);
            } else {
              setSelectedStamp(parseInt(stmp, 10));
              confirmSelectedNumber(stmp);
              if (maxStampIssued > 8) {
                setStampArray(initStampArray);
              }
            }
          }}
        />
      </div>
    ));
  };

  return (
    <FullScreenDialog
      open={open}
      onClose={onClose}
      PaperComponent={DialogPaper}
    >
      <div className={classes.box}>
        <div className={classes.close}>
          <IconButton onClick={onClose}>
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </div>
      </div>
      <div className={classes.cardContainer}>
        <Typography variant="h2" className={classes.title} gutterBottom>
          Number of stamps to issue
        </Typography>
        <div className={classes.stampContainer}>{renderStamps()}</div>
        <SelectOtherNumberDialog
          open={selectOtherNumberOpenDialog}
          onClose={() => setSelectOtherNumberOpenDialog(false)}
          confirmSelectedNumber={(selectNum: string) => {
            let updateStampArray = stampArray;
            updateStampArray.pop();
            updateStampArray.push(selectNum);
            setStampArray(updateStampArray);
            setSelectOtherNumberOpenDialog(false);
          }}
          maxSelectedNumber={maxStampIssued}
        />
      </div>
    </FullScreenDialog>
  );
}
